import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FullScreenCircularProgress from '@src/new-components/Progress/FullScreenCircularProgress';
import apis from '@src/apis';
import actions from '@src/redux/actions';
import { VOICE_OWNERSHIP } from '@src/constants/voice';
import { MOBILE_OS } from '@src/constants/os';
import { getMobileOperatingSystem } from '@src/utils/osMobile';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import { MAX_VOICE_CLONING_TO_FETCH } from '@src/constants/voiceCloning';
import VoiceCloningList from './VoiceCloningList';
import Home from './Home';
import VoiceCloningMobile from './VoiceCloningMobile';
import VoiceCloneInput from '../VoiceCloneInput';
import ProfessionalVoiceCloneUpload from '../ProfessionalVoiceCloneUpload';

const NewVoiceCloning = () => {
  const dispatch = useDispatch();
  const [voices, setVoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [os, setOs] = useState(null);
  const [isFetchingVoices, setIsFetchingVoices] = useState(true);
  const [showPopupInstantVoiceCloning, setShowPopupInstantVoiceCloning] =
    useState(false);
  const [voiceInfo, setVoiceInfo] = useState(null);
  const [
    showPopupProfessionalVoiceCloneUpload,
    setShowPopupProfessionalVoiceCloneUpload,
  ] = useState(false);
  const { refetchVoicesList } = useSelector((state) => state.voiceVoiceCloning);
  const enrichVoiceCloningWithBadge = (voiceCloningVoices, ttsVoices) => {
    const ttsVoicesMap = new Map(ttsVoices.map((voice) => [voice.code, voice]));

    return voiceCloningVoices.map((voiceCloningVoice) => {
      const correspondingTtsVoice = ttsVoicesMap.get(voiceCloningVoice.code);

      if (correspondingTtsVoice) {
        return {
          ...voiceCloningVoice,
          badge: correspondingTtsVoice?.badge,
        };
      }

      return voiceCloningVoice;
    });
  };

  const fetchVoices = async () => {
    const [responseVoiceCloning, responseVoicesV3] = await Promise.all([
      apis.voiceCloning.getVoiceCloningVoices(),
      apis.voices.getVoicesV3({
        voiceOwnership: VOICE_OWNERSHIP.PERSONAL,
        limit: MAX_VOICE_CLONING_TO_FETCH,
      }),
    ]);

    if (!responseVoiceCloning?.status || !responseVoicesV3?.status) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'getVoicesFailed',
        }),
      );
      return;
    }

    const voiceCloningVoices = responseVoiceCloning?.result?.voices || [];
    const ttsVoices = responseVoicesV3?.result?.voices || [];
    setVoices(enrichVoiceCloningWithBadge(voiceCloningVoices, ttsVoices));
    setTotal(responseVoiceCloning?.result?.total);
    setIsFetchingVoices(false);
  };

  const prevRefetchVoicesList = React.useRef(refetchVoicesList);

  const handleContinueUploadVoice = (newVoiceInfo) => {
    setVoiceInfo(newVoiceInfo);
    setShowPopupProfessionalVoiceCloneUpload(true);
  };

  const handleClosePopupProfessionalVoiceCloneUpload = () => {
    setShowPopupProfessionalVoiceCloneUpload(false);
    setVoiceInfo(null);
  };

  useEffect(() => {
    fetchVoices();
    const osDetected = getMobileOperatingSystem();
    setOs(osDetected);
    prevRefetchVoicesList.current = refetchVoicesList;
  }, []);

  useEffect(() => {
    if (prevRefetchVoicesList.current === false && refetchVoicesList === true) {
      fetchVoices();
      dispatch(actions.voiceVoiceCloning.refreshVoicesList(false));
    }
    prevRefetchVoicesList.current = refetchVoicesList;
  }, [refetchVoicesList]);

  useEffect(() => {
    setTotal(voices?.length);
  }, [voices]);

  if (isFetchingVoices || !os) {
    return <FullScreenCircularProgress size={45} />;
  }

  if (os !== MOBILE_OS.UNKNOWN) {
    return <VoiceCloningMobile />;
  }

  return (
    <>
      {total === 0 ? (
        <Home
          handleOpenPopupInstantVoiceCloning={() =>
            setShowPopupInstantVoiceCloning(true)
          }
          handleOpenPopupProfessionalVoiceCloneUpload={() =>
            setShowPopupProfessionalVoiceCloneUpload(true)
          }
        />
      ) : (
        <VoiceCloningList
          voices={voices}
          handleOpenPopupInstantVoiceCloning={() =>
            setShowPopupInstantVoiceCloning(true)
          }
          handleOpenPopupProfessionalVoiceCloneUpload={() =>
            setShowPopupProfessionalVoiceCloneUpload(true)
          }
          onContinueUploadVoice={handleContinueUploadVoice}
          setVoiceList={setVoices}
        />
      )}
      {showPopupInstantVoiceCloning && (
        <CommonDialog
          contentComponent={
            <VoiceCloneInput
              refreshVoices={fetchVoices}
              closePopupInstantVoiceCloning={() =>
                setShowPopupInstantVoiceCloning(false)
              }
            />
          }
          open={showPopupInstantVoiceCloning}
        />
      )}
      {showPopupProfessionalVoiceCloneUpload && (
        <CommonDialog
          contentComponent={
            <ProfessionalVoiceCloneUpload
              closePopup={handleClosePopupProfessionalVoiceCloneUpload}
              refetchVoicesList={fetchVoices}
              voiceInfo={voiceInfo}
            />
          }
          open={showPopupProfessionalVoiceCloneUpload}
        />
      )}
    </>
  );
};

export default NewVoiceCloning;
